import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Layout from "@/components/layout/Layout";
import { api, shortHash, shortAddr, timeAgo, formatNumber } from "@/lib/api";
import { Box, Copy, Check } from "lucide-react";
import { toast } from "sonner";

const CopyBtn = ({ text }) => {
  const [ok, setOk] = useState(false);
  return (
    <button
      onClick={() => { navigator.clipboard.writeText(text); setOk(true); setTimeout(() => setOk(false), 1200); }}
      className="ml-2 inline-flex items-center text-[var(--ks-text-soft)] hover:text-[var(--ks-primary)]"
      aria-label="copy"
      data-testid="copy-btn"
    >
      {ok ? <Check size={14} /> : <Copy size={14} />}
    </button>
  );
};

const DetailRow = ({ label, children, testId }) => (
  <div className="ks-detail-row" data-testid={testId}>
    <div className="ks-detail-label">{label}</div>
    <div className="ks-detail-value">{children}</div>
  </div>
);

export default function BlockDetail() {
  const { number } = useParams();
  const [data, setData] = useState(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    api.get(`/blocks/${number}`)
      .then((r) => setData(r.data))
      .catch(() => setNotFound(true));
  }, [number]);

  if (notFound) return <Layout><p className="text-lg" data-testid="block-notfound">Block not found</p></Layout>;
  if (!data) return <Layout><p>Loading…</p></Layout>;

  const { block, transactions } = data;

  return (
    <Layout>
      <div className="flex items-center gap-3 mb-4">
        <Box className="text-[var(--ks-primary)]" />
        <h1 className="text-xl font-semibold" data-testid="block-title">Block #{formatNumber(block.number)}</h1>
      </div>

      <div className="ks-card">
        <div className="ks-card-header"><span>Overview</span></div>
        <DetailRow label="Block Height" testId="block-height">
          <span className="font-semibold">{formatNumber(block.number)}</span>
        </DetailRow>
        <DetailRow label="Timestamp" testId="block-timestamp">
          {timeAgo(block.timestamp)} · {new Date(block.timestamp * 1000).toUTCString()}
        </DetailRow>
        <DetailRow label="Transactions" testId="block-txcount">
          <span className="ks-badge">{block.tx_count} transactions</span>
        </DetailRow>
        <DetailRow label="Validator" testId="block-validator">
          <Link to={`/address/${block.miner}`} className="ks-link mono">{block.miner}</Link>
          <CopyBtn text={block.miner} />
        </DetailRow>
        <DetailRow label="Block Reward" testId="block-reward">{block.reward_eth} ETH</DetailRow>
        <DetailRow label="Gas Used" testId="block-gas-used">
          {formatNumber(block.gas_used)} / {formatNumber(block.gas_limit)} <span className="text-[var(--ks-text-soft)]">({((block.gas_used / block.gas_limit) * 100).toFixed(1)}%)</span>
        </DetailRow>
        <DetailRow label="Base Fee" testId="block-base-fee">{block.base_fee_gwei} Gwei</DetailRow>
        <DetailRow label="Block Hash" testId="block-hash">
          <span className="mono break-all">{block.hash}</span>
          <CopyBtn text={block.hash} />
        </DetailRow>
        <DetailRow label="Parent Hash" testId="block-parent">
          <span className="mono break-all">{block.parent_hash}</span>
        </DetailRow>
        <DetailRow label="Size" testId="block-size">{formatNumber(block.size)} bytes</DetailRow>
      </div>

      <div className="ks-card mt-6">
        <div className="ks-card-header"><span>Transactions in this block</span></div>
        <div className="overflow-x-auto">
          <table className="ks-table">
            <thead>
              <tr>
                <th>Txn Hash</th><th>From</th><th>To</th><th>Method</th><th className="text-right">Value</th>
              </tr>
            </thead>
            <tbody>
              {transactions.map((t) => (
                <tr key={t.hash} data-testid={`blocktx-${t.hash}`}>
                  <td><Link to={`/tx/${t.hash}`} className="ks-link mono">{shortHash(t.hash)}</Link></td>
                  <td><Link to={`/address/${t.from_address}`} className="ks-link mono">{shortAddr(t.from_address)}</Link></td>
                  <td><Link to={`/address/${t.to_address}`} className="ks-link mono">{shortAddr(t.to_address)}</Link></td>
                  <td>{t.method}</td>
                  <td className="text-right">{t.value_eth} ETH</td>
                </tr>
              ))}
              {transactions.length === 0 && (
                <tr><td colSpan={5} className="text-center py-6 text-[var(--ks-text-soft)]">No transactions in this block.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}
